import * as Sentry from '@sentry/react'
import { extraErrorDataIntegration } from '@sentry/react'
import type { ReplayFrameEvent } from '@sentry/react'
import { INVALID_AUTHENTICATION_MESSAGE } from '~/constants/authentication'
import {
  getObservabilitySessionId,
  OBSERVABILITY_SESSION_ID_KEY,
} from '~/services/observability/correlation'
import {
  getTracePropagationTargets,
  tracesSampler,
} from '~/services/observability/tracing'
import {
  getAppVersion,
  isElectron,
  isProductionBuildEnvironment,
} from '~/utils/common'

import {
  desktopReleaseName,
  desktopSentryEnvironment,
  ELECTRON_DEV_DSN,
  ELECTRON_PROD_DSN,
  SENTRY_DATA_COLLECTION,
} from '@mangovoice/constants'
import { createCaptureException, isBotUserAgent } from '@mangovoice/core'

enum SuppressionType {
  RegExp,
  ExactMatch,
  Contains,
  StartsWith,
}

interface MessageSuppression {
  value: string | RegExp
  type: SuppressionType
}

const errorMessagesToSuppress: Array<MessageSuppression> = [
  // We get a lot of WebSocket closed 1006 errors that are not actionable
  {
    value: /^WebSocket closed \S+:\d+ \(code: 1006\)$/,
    type: SuppressionType.RegExp,
  },
  // This error message is expected with Vite and can be ignored. It just means someone hasn't reloaded the page since a deploy and this will trigger a reload automatically
  {
    value: 'error loading dynamically imported module:',
    type: SuppressionType.StartsWith,
  },
  // Safari's wording for the same stale-chunk-after-deploy case above (Chrome
  // says "error loading dynamically imported module"). Same cause, not actionable.
  {
    value: 'Importing a module script failed',
    type: SuppressionType.Contains,
  },
  {
    value: 'ResizeObserver',
    type: SuppressionType.Contains,
  },
  {
    value: 'Failed to fetch',
    type: SuppressionType.StartsWith,
  },
  // Safari's wording for a failed fetch (Chrome says "Failed to fetch"). Network
  // noise, not actionable — includes the CloudWatch logger's own send failures.
  {
    value: 'Load failed',
    type: SuppressionType.StartsWith,
  },
  // Cross-origin iframe access, thrown by the embedded Salesforce chat frame.
  // 0 users impacted; not caused by Mango Voice code and not actionable.
  {
    value: 'Blocked a frame with origin',
    type: SuppressionType.Contains,
  },
  // This error occurs when users navigate between call recordings quickly, causing play() to be
  // interrupted by pause(). This is expected behavior and not actionable.
  // See: https://developers.google.com/web/updates/2017/06/play-request-was-interrupted
  {
    value: 'The play() request was interrupted',
    type: SuppressionType.Contains,
  },
  // Expected when the API invalidates the session; interceptor forces logout — not actionable in Sentry
  {
    value: INVALID_AUTHENTICATION_MESSAGE,
    type: SuppressionType.Contains,
  },
  // iOS WKWebView emits this when a WKScriptMessageHandlerWithReply never calls its
  // replyHandler. Triggered by third-party scripts or in-app browsers (Instagram, Facebook,
  // etc.) — not by Mango Voice code. Not actionable.
  {
    value: 'WKWebView API client did not respond to this postMessage',
    type: SuppressionType.Contains,
  },
  // Browser extensions (e.g. Safari/Edge) inject code referencing variables that don't exist in our
  // bundle. These ReferenceErrors are not actionable. See: https://github.com/getsentry/sentry-javascript/issues/8444
  {
    value: "Can't find variable: EmptyRanges",
    type: SuppressionType.Contains,
  },
  // Salesforce Embedded Messaging SDK internal error — occurs when the SDK's async
  // initialization tries to read deployment config before it's available. Not actionable.
  {
    value: "Cannot read properties of undefined (reading 'showChatButton')",
    type: SuppressionType.ExactMatch,
  },
  // getUserMedia when the mic/headset is unplugged or the OS revokes access.
  // Expected hardware state, handled in the call UI — not actionable in Sentry.
  {
    value: 'Requested device not found',
    type: SuppressionType.Contains,
  },
  // CloudWatch logger self-report: the log stream is created lazily, so a send
  // can briefly race ahead of stream creation. The logger retries; not actionable.
  {
    value: 'The specified log stream does not exist',
    type: SuppressionType.Contains,
  },
]

export const shouldSuppressError = (message: string): boolean => {
  return errorMessagesToSuppress.some((messageToSuppress) => {
    switch (messageToSuppress.type) {
      case SuppressionType.RegExp:
        return (messageToSuppress.value as RegExp).test(message)
      case SuppressionType.ExactMatch:
        return message === messageToSuppress.value
      case SuppressionType.Contains:
        return message.includes(messageToSuppress.value as string)
      case SuppressionType.StartsWith:
        return message.startsWith(messageToSuppress.value as string)
    }
  })
}

/**
 * Sentry's DOM breadcrumbs describe the clicked or typed-in element with its
 * `aria-label`, `title` and `alt`. Those hold user data in places (fax
 * attachment file names, confirmation email addresses), so their values are
 * replaced before a `ui.*` breadcrumb is kept. Values are unescaped, so the
 * match ends only where an attribute Sentry writes after these (`type`,
 * `name`, `title`, `alt`), the next element or the string begins.
 */
const NAMING_ATTRIBUTE =
  /\[(aria-label|title|alt)="[\s\S]*?"\](?=\[(?:type|name|title|alt)="|\s>\s|$)/g

export const scrubUiBreadcrumb = (
  breadcrumb: Sentry.Breadcrumb,
): Sentry.Breadcrumb => {
  if (!breadcrumb.category?.startsWith('ui.') || !breadcrumb.message) {
    return breadcrumb
  }
  return {
    ...breadcrumb,
    message: breadcrumb.message.replace(NAMING_ATTRIBUTE, '[$1="[Filtered]"]'),
  }
}

/**
 * Session Replay records its own `ui.*` breadcrumb frames, which skip
 * `beforeBreadcrumb`, so they get the same scrubbing.
 */
export const scrubReplayFrame = (event: ReplayFrameEvent): ReplayFrameEvent => {
  if (event.data.tag !== 'breadcrumb') return event
  const { payload } = event.data
  if (!payload.category?.startsWith('ui.')) return event
  return {
    ...event,
    data: {
      ...event.data,
      payload: scrubUiBreadcrumb(payload) as typeof payload,
    },
  }
}

function getDsn() {
  const developmentDsn =
    'https://f5f22fb6751b4ee582afaad0159d03d0@o372418.ingest.sentry.io/6167647'
  const productionDsn =
    'https://b010d3f8ae354228830c828729a220ca@o372418.ingest.sentry.io/5883539'

  if (isElectron) {
    // Electron DSNs are shared with the main process (electron-app sentry-main.ts).
    return isProductionBuildEnvironment() ? ELECTRON_PROD_DSN : ELECTRON_DEV_DSN
  }

  return isProductionBuildEnvironment() ? productionDsn : developmentDsn
}

function getSessionSampleRate() {
  // https://docs.sentry.io/platforms/javascript/guides/electron/session-replay/#sampling
  // Guidance says 1.0 in dev and 0.1 in prods is a good rate.

  const developmentSampleRate = 0.0
  const productionSampleRate = 0.0
  const electronDevSampleRate = 0.0
  const electronProdSampleRate = 0.0

  if (isElectron) {
    return isProductionBuildEnvironment()
      ? electronProdSampleRate
      : electronDevSampleRate
  }

  return isProductionBuildEnvironment()
    ? productionSampleRate
    : developmentSampleRate
}

function getErrorSampleRate() {
  // https://docs.sentry.io/platforms/javascript/guides/electron/session-replay/#sampling
  // Sentry guidance suggests 0.1 in prod, but the @sentry/react v7→v10 migration
  // (#1631) drove the Replay quota up — same rate now produces meaningfully more
  // replays. Dropped to 0.01 to bring usage back under budget; revisit after
  // observing actual Replay volume.

  const developmentSampleRate = 0.0
  const productionSampleRate = 0.01
  const electronDevSampleRate = 0.0
  const electronProdSampleRate = 0.01

  if (isElectron) {
    return isProductionBuildEnvironment()
      ? electronProdSampleRate
      : electronDevSampleRate
  }

  return isProductionBuildEnvironment()
    ? productionSampleRate
    : developmentSampleRate
}

// Injected at build time by vite.config.ts `define` so the runtime release name
// matches the sentryVitePlugin release name (required for source map resolution).
declare const __SENTRY_RELEASE_CHANNEL__: string

function getSentryReleaseName(version: string): string {
  if (!isElectron) {
    return `web-app@${version}`
  }
  // Desktop release name shared with the main process + build configs.
  const channel =
    typeof __SENTRY_RELEASE_CHANNEL__ === 'string'
      ? __SENTRY_RELEASE_CHANNEL__
      : undefined
  return desktopReleaseName(version, channel)
}

// Resolved before init, unlike the channel tag below: the session backing
// crash-free rate carries only release + environment, so a channel applied
// afterwards never reaches release health. Costs no extra startup latency —
// it resolves alongside the version lookup init already waits on.
//
// Bounded because init now waits on it: a try/catch covers a rejection but not
// an invoke that never settles, and a wedged channel handler must not be able
// to take crash reporting down with it. Losing the channel costs the per-channel
// split; losing Sentry costs everything.
const CHANNEL_LOOKUP_TIMEOUT_MS = 2000

const getDesktopChannel = async (): Promise<string | undefined> => {
  if (!isElectron) {
    return undefined
  }
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      window.electron?.channels?.getCurrentChannel?.(),
      new Promise<undefined>((resolve) => {
        timer = setTimeout(() => resolve(undefined), CHANNEL_LOOKUP_TIMEOUT_MS)
      }),
    ])
  } catch {
    return undefined
  } finally {
    clearTimeout(timer)
  }
}

// Set by the desktop E2E suite (apps/electron-app/src/e2e-config.ts) so test
// runs of packaged dev builds don't report to the production project.
const TELEMETRY_LOOKUP_TIMEOUT_MS = 2000

const isDesktopTelemetryDisabled = async (): Promise<boolean> => {
  if (!isElectron) {
    return false
  }
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return (
      (await Promise.race([
        window.electron?.invoke?.('app:is-telemetry-disabled'),
        new Promise<false>((resolve) => {
          timer = setTimeout(() => resolve(false), TELEMETRY_LOOKUP_TIMEOUT_MS)
        }),
      ])) === true
    )
  } catch {
    return false
  } finally {
    clearTimeout(timer)
  }
}

interface SentryOptions {
  replay?: boolean
  tags?: Record<string, string>
}

export const initiateSentry = ({
  replay = true,
  tags = {},
}: SentryOptions = {}) => {
  const doGetAppVersion = async (): Promise<string> => {
    return await getAppVersion()
  }

  Promise.all([
    doGetAppVersion(),
    getDesktopChannel(),
    isDesktopTelemetryDisabled(),
  ]).then(
    ([version, desktopChannel, telemetryDisabled]: [
      string,
      string | undefined,
      boolean,
    ]) => {
      if (telemetryDisabled) {
        return
      }
      Sentry.init({
        dsn: getDsn(),
        // Left unset off desktop so the web project keeps Sentry's own default.
        ...(isElectron
          ? { environment: desktopSentryEnvironment(desktopChannel) }
          : {}),
        dataCollection: SENTRY_DATA_COLLECTION,
        // SDK v11 streams spans by default, which drops scope tags (channel,
        // observability session id) from spans and renames unrouted pageloads
        // to "Pageload". Static keeps transactions as the tracing budget and
        // the PostHog pivot expect.
        traceLifecycle: 'static',
        integrations: [
          Sentry.browserTracingIntegration(),
          // v11 defaults to one session per page load. Desktop windows stay
          // open all day, so that would change the crash-free denominators the
          // release-health alerts are tuned to.
          Sentry.browserSessionIntegration({ lifecycle: 'route' }),
          // eventTarget wrapping patches addEventListener, which makes the
          // Salesforce Embedded Messaging SDK log repeated console warnings.
          // Other instrumentation (setTimeout, requestAnimationFrame, XHR) stays on.
          Sentry.browserApiErrorsIntegration({ eventTarget: false }),
          ...(replay
            ? [
                Sentry.replayIntegration({
                  maskAllText: true,
                  beforeAddRecordingEvent: scrubReplayFrame,
                }),
              ]
            : []),
          extraErrorDataIntegration({
            depth: 9,
            captureErrorCause: false,
          }),
        ],
        normalizeDepth: 10,
        release: getSentryReleaseName(version),
        tracesSampler,
        // Headers attach on every matching request regardless of the sampling
        // decision, so these targets are a CORS contract — see tracing.ts for
        // why mango-api is not among them.
        tracePropagationTargets: getTracePropagationTargets(),
        propagateTraceparent: true,
        replaysSessionSampleRate: getSessionSampleRate(),
        replaysOnErrorSampleRate: getErrorSampleRate(),
        beforeBreadcrumb: scrubUiBreadcrumb,
        beforeSend: (event) => {
          // Drop events from automated clients (uptime checks, crawlers, scanners).
          if (isBotUserAgent(navigator.userAgent)) {
            return null
          }

          if (event.exception) {
            const errorString = event.exception.values?.[0]?.value
            if (errorString && shouldSuppressError(errorString)) {
              return null
            }
          }

          return event
        },
      })

      // Correlation id shared with PostHog for cross-tool pivoting.
      Sentry.setTag(OBSERVABILITY_SESSION_ID_KEY, getObservabilitySessionId())
      Sentry.setTags(tags)

      // For Electron: Set channel and partner tags so we can filter errors in Sentry
      if (isElectron) {
        if (desktopChannel) {
          Sentry.setTag('channel', desktopChannel)
        }
        const partnerPromise =
          window.electron?.getPartnerId?.() ?? Promise.resolve(undefined)
        partnerPromise
          .then((partnerId) => {
            if (partnerId && partnerId !== 'default') {
              Sentry.setTag('partner', partnerId)
            }
            Sentry.setContext('app', {
              channel: desktopChannel,
              version,
              platform: 'electron-renderer',
              ...(partnerId && partnerId !== 'default'
                ? { partner: partnerId }
                : {}),
            })
          })
          .catch(() => {
            // Ignore errors - partner info is nice-to-have
          })
      } else {
        // Web build - set channel to 'web'
        Sentry.setTag('channel', 'web')
        Sentry.setContext('app', {
          channel: 'web',
          version,
          platform: 'web',
        })
      }
    },
  )
}

export const captureException = createCaptureException(Sentry)

export const addBreadCrumb = (breadCrumb: Sentry.Breadcrumb) => {
  Sentry.addBreadcrumb({
    level: breadCrumb.level ?? 'log',
    ...breadCrumb,
  })
}

export { Sentry }
